import { DragEvent, ReactElement, useCallback, useMemo, useState } from 'react'

import { toggleSetItem } from '../../utils/toggleSetItem'
import { TreeNode, TreeNodeItem } from './elements/TreeNodeItem'

export type { TreeNode }

export interface ToggleTreeProps {
  expandedIds?: Set<string>
  tree: TreeNode[]
  onNodeDragOver?: (id: string, event: DragEvent) => void
  onNodeDrop?: (id: string, event: DragEvent) => void
  onSelect: (id: string) => void
  onToggle?: (id: string) => void
  selectedId?: string
}

export const ToggleTree = ({
  expandedIds: externalExpandedIds,
  tree,
  onNodeDragOver,
  onNodeDrop,
  onSelect,
  onToggle: externalOnToggle,
  selectedId,
}: ToggleTreeProps): ReactElement => {
  const [internalExpandedIds, setInternalExpandedIds] = useState(new Set<string>())

  const expandedIds = externalExpandedIds ?? internalExpandedIds

  const nodes = useMemo(() => Object.fromEntries(tree.map((node) => [node.id, node])), [tree])

  const rootIds = useMemo(
    () => tree.filter((node) => !node.parentId || !nodes[node.parentId]).map((node) => node.id),
    [tree, nodes],
  )

  const handleNodeToggle = useCallback(
    (id: string) => {
      if (externalOnToggle) {
        externalOnToggle(id)
      } else {
        setInternalExpandedIds((prev) => toggleSetItem(prev, id))
      }
    },
    [externalOnToggle],
  )

  return (
    <ul>
      {rootIds.map((id) => (
        <TreeNodeItem
          key={id}
          expandedIds={expandedIds}
          id={id}
          nodes={nodes}
          onNodeDragOver={onNodeDragOver}
          onNodeDrop={onNodeDrop}
          onSelect={onSelect}
          onToggle={handleNodeToggle}
          selectedId={selectedId}
        />
      ))}
    </ul>
  )
}
